import { memo } from "react"
import { useTranslation } from "react-i18next"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/app/components/ui/dialog"
import { Button } from "@/app/components/ui/button"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SectionSwipeDialogMode = "complete" | "delete"

interface SectionSwipeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  mode: SectionSwipeDialogMode
  sectionTitle: string
  childCount: number
  /** Complete/delete only the section */
  onSectionOnly: () => void
  /** Complete/delete the section AND all children */
  onWithChildren: () => void
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Confirmation dialog shown when swiping a section to complete or delete.
 *
 * Three actions:
 *   - Cancel (close dialog)
 *   - "Just section" — action applies only to the section
 *   - "Section and N tasks" — action applies to section + all children
 */
export const SectionSwipeDialog = memo(function SectionSwipeDialog({
  open,
  onOpenChange,
  mode,
  sectionTitle,
  childCount,
  onSectionOnly,
  onWithChildren,
}: SectionSwipeDialogProps) {
  const { t } = useTranslation()

  const title = sectionTitle.trim() || t("Untitled")

  const isComplete = mode === "complete"
  const dialogTitle = isComplete ? t("Complete all?") : t("Delete all?")
  const dialogDescription = isComplete
    ? t(
        "Do you want to complete all items under {{title}} or just the section itself?",
        { title },
      )
    : t(
        "Do you want to delete all items under {{title}} or just the section itself?",
        { title },
      )

  const withChildrenLabel = isComplete
    ? t("Section and {{count}} tasks", { count: childCount })
    : t("Section and {{count}} tasks", { count: childCount })

  const sectionOnlyLabel = t("Just section")

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogDescription>{dialogDescription}</DialogDescription>
        </DialogHeader>

        <DialogFooter className="flex gap-2 sm:justify-end">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("Cancel")}
          </Button>
          {childCount > 0 && (
            <Button
              variant={isComplete ? "secondary" : "error"}
              onClick={onWithChildren}
            >
              {withChildrenLabel}
            </Button>
          )}
          <Button
            variant={isComplete ? "primary" : "secondary"}
            onClick={onSectionOnly}
          >
            {sectionOnlyLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
})
